import React, { useState, useEffect } from "react";
import LoadingSpinner from "../../Controls/Spinner/LoadingSpinner";
import moment from "moment";
import APIRequest from "../../../Services/commonAPIConsumer";
import InfoToolTip from "./InfoIconTooltip";
import {
  excludePendingFullScreenRecords,
  filterStatisticsRows,
} from "./openRecordFilters";

export default function ManageStatistics({
  item,
  selectedLanguage,
  timeZoneAbbrevation,
  timeZoneRef,
  applyTimeZoneToDisplay,
  dateFormatValueUpper,
}) {
  const [docVersions, setDocVersions] = useState([]);
  const [selectedVersionModal, setSelectedVersionModal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [statsDataMap, setStatsDataMap] = useState({});
  const getTimeFormat = (seconds) => {
    var time = "00:00:00";
    if (seconds != null) {
      var sec = parseInt(seconds, 10);
      var hours = Math.floor(sec / 3600);
      var minutes = Math.floor((sec - hours * 3600) / 60);
      sec = sec - hours * 3600 - minutes * 60;
      if (hours < 10) hours = "0" + hours;
      if (minutes < 10) minutes = "0" + minutes;
      if (sec < 10) sec = "0" + sec;
      time = hours + ":" + minutes + ":" + sec;
    }
    return time;
  };

  const fetchVersions = async () => {
    setIsLoading(true);
    try {
      const records = item?.versionHistoryRecords;
      const rawList = Array.isArray(records) ? records : (records ? [records] : []);

      if (rawList.length > 0) {
        rawList.forEach((ver) => {
          let totalKB = 0;
          let validFound = false;
          if (ver.inputFiles && ver.inputFiles.length > 0) {
            ver.inputFiles.forEach(file => {
              const sizeStr = file.fileSize?.toString().trim().toLowerCase();
              if (!sizeStr) return;
              const sizeValue = parseFloat(sizeStr);
              if (isNaN(sizeValue)) return;
              validFound = true;
              if (sizeStr.includes("mb")) totalKB += sizeValue * 1024;
              else totalKB += sizeValue;
            });
          }
          ver.originalSizeFormatted = validFound ? (totalKB >= 1024 ? `${Math.round(totalKB / 1024)} MB` : `${Math.round(totalKB)} KB`) : "0 KB";
        });

        const sortedDesc = [...rawList].sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0));
        setDocVersions(sortedDesc);
      } else {
        setDocVersions([]);
      }
    } catch (error) {
      console.error("Error fetching versions for Statistics:", error);
      setDocVersions([]);
    } finally {
      setIsLoading(false);
    }
  };
  const versionHistoryRecords = item?.versionHistoryRecords;

  useEffect(() => {
    if (item && item.documentId) {
      fetchVersions();
    }
  }, [item?.documentId, versionHistoryRecords]);

  const activityLogDateFormat = () => {
    const baseFormat = dateFormatValueUpper && dateFormatValueUpper !== "" ? dateFormatValueUpper : "MM/DD/YYYY";
    return `${baseFormat} HH:mm`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    const dateValue = new Date(timeStr);
    if (isNaN(dateValue.getTime())) return "-";
    return moment
      .utc(applyTimeZoneToDisplay(dateValue, timeZoneRef.current))
      .format(activityLogDateFormat());
  };

  const getFileSizeFormatted = (ver) => {
    let totalKB = 0;
    if (ver?.inputFiles && ver.inputFiles.length > 0) {
      ver.inputFiles.forEach(file => {
        const sizeStr = file.fileSize?.toString().trim().toLowerCase();
        if (!sizeStr) return;
        const sizeValue = parseFloat(sizeStr);
        if (!isNaN(sizeValue)) {
          totalKB += sizeStr.includes("mb") ? sizeValue * 1024 : sizeValue;
        }
      });
    }
    if (totalKB > 0) {
      return totalKB >= 1024 ? `${Math.round(totalKB / 1024)} MB` : `${Math.round(totalKB)} KB`;
    }
    return ver?.originalSizeFormatted || ver?.fileSize || "0 KB";
  };

  const getPagesCount = (ver) => ver?.pages || ver?.pagesCount || 0;

  const versionByNumMap = new Map();
  docVersions.forEach(ver => {
    versionByNumMap.set(ver.versionNumber, ver);
  });

  const versionSizePagesMap = new Map();
  Array.from(versionByNumMap.keys()).sort((a, b) => a - b).forEach((vNum) => {
    const currentVer = versionByNumMap.get(vNum);
    const currentCombined = `${getFileSizeFormatted(currentVer)} / ${getPagesCount(currentVer)}`;

    if (vNum === 1 || !versionByNumMap.has(vNum - 1)) {
      versionSizePagesMap.set(vNum, { origDisplay: currentCombined, updatedDisplay: "N/A" });
    } else {
      const prevVer = versionByNumMap.get(vNum - 1);
      const prevCombined = `${getFileSizeFormatted(prevVer)} / ${getPagesCount(prevVer)}`;
      versionSizePagesMap.set(vNum, { origDisplay: prevCombined, updatedDisplay: currentCombined });
    }
  });

  const handleOpenVersionModal = (version) => {
    setSelectedVersionModal(version);
    fetchVersionPageStats(version);
  };

  const fetchVersionPageStats = async (version) => {
    if (!version) return;

    setIsStatsLoading(true);
    try {
      let versionOpenRecords = [];
      // PendingFullScreen open records never produced a real reading session, their page
      // rows must not be pulled into the statistics.
      const directRecs = excludePendingFullScreenRecords(version?.openRecords);
      if (directRecs.length > 0) {
        versionOpenRecords.push(...directRecs);
      }
      const versionOpenRecordIds = versionOpenRecords.map(r => r.id).filter(Boolean);

      const baseUrl = `${process.env.REACT_APP_API}${process.env.REACT_APP_API_GetStatisticsData}/${item.documentId}`;
      const params = new URLSearchParams();
      if (versionOpenRecordIds.length > 0) {
        params.append("openRecordId", versionOpenRecordIds.join(","));
      }
      if (version.versionNumber != null) {
        params.append("VersionNumber", version.versionNumber.toString());
      }
      const url = `${baseUrl}?${params.toString()}`;
      const jsonDocs = await APIRequest.GET("", url);

      if (jsonDocs && Array.isArray(jsonDocs)) {
        const pageStatsMap = new Map();
        // Drops the rows belonging to a PendingFullScreen session and the empty
        // "page 1 / 00:00:00 / 1 read per session" artefact they leave behind.
        const usableStats = filterStatisticsRows(jsonDocs, version?.openRecords);
        usableStats.forEach(stat => {
          const pageNum = stat.pageNumber;
          if (!pageNum) return;

          const hours = stat.totalHours;
          const minutes = stat.totalMinutes;
          const secondsRem = stat.totalSeconds;
          const seconds = hours * 3600 + minutes * 60 + secondsRem;

          const pageReads = stat.totalReads;
          const readsPerSession = stat.totalReadsPerSession;

          if (pageStatsMap.has(pageNum)) {
            const existing = pageStatsMap.get(pageNum);
            existing.totalSeconds += seconds;
            existing.totalPageReads += pageReads;
            existing.readsPerSession += readsPerSession;
          } else {
            pageStatsMap.set(pageNum, {
              pageNumber: pageNum,
              totalSeconds: seconds,
              totalPageReads: pageReads,
              readsPerSession: readsPerSession
            });
          }
        });

        const result = Array.from(pageStatsMap.values()).sort((a, b) => a.pageNumber - b.pageNumber);
        setStatsDataMap(prev => ({
          ...prev,
          [version.versionNumber]: result
        }));
      }
    } catch (error) {
      console.error("Error fetching version page stats:", error);
    } finally {
      setIsStatsLoading(false);
    }
  };

  return (
    <div className="pb-3 mb-1 w-100">
      {/* Main Version Statistics Summary Table */}
      <div className="innertable-responsive activityLogtable-container mb-2 w-100 border overflow-hidden rounded-3 stats-table-container">
        <table className="table mb-0 bg-white w-100 stats-table">
          <thead className="bg-lightred">
            <tr>
              <th className="borderRight font-13 stats-th-ver">
                {selectedLanguage?.LstLanguageCodes?.UI_versionNo}
              </th>
              <th className="borderRight font-13 stats-th-orig">
                {selectedLanguage?.LstLanguageCodes?.UI_OriginalSize}/{selectedLanguage?.LstLanguageCodes?.UI_Pages}
              </th>
              <th className="borderRight font-13 stats-th-upd">
                {selectedLanguage?.LstLanguageCodes?.UI_UpdatedSize}/{selectedLanguage?.LstLanguageCodes?.UI_Pages}
              </th>
              <th className="borderRight font-13 stats-th-user">
                {selectedLanguage?.LstLanguageCodes?.UI_UpdatedBy}
              </th>
              <th className="borderRight font-13 stats-th-time">
                {(selectedLanguage?.LstLanguageCodes?.UI_UpdatedTime) + " (" + (timeZoneAbbrevation?.current) + ")"}
              </th>
              <th className="text-center font-13 stats-th-key">
                {selectedLanguage?.LstLanguageCodes?.UI_ReadersSnapshot}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && docVersions.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center p-4">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : docVersions.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center p-4">
                  <div className="no-record mt-n1 d-flex align-items-center justify-content-center w-100">
                    <span className="font-13">{selectedLanguage?.LstLanguageCodes?.UI_NoRecordsFound}</span>
                  </div>
                </td>
              </tr>
            ) : (
              docVersions.map((version, idx) => {
                const verId = version?.id || version?.versionNumber || version?.versionNo;
                const displayVerNo = version.versionNumber != null
                  ? "V" + version.versionNumber
                  : (version.versionNo ? "V" + version.versionNo : "V" + (docVersions.length - idx));

                const vNumKey = version.verNum || version.versionNumber || 1;
                const sizeInfo = versionSizePagesMap.get(vNumKey) || {
                  origDisplay: `${getFileSizeFormatted(version)} / ${getPagesCount(version)}`,
                  updatedDisplay: (version.versionNumber === 1 || version.versionNo === "1" || version.versionNo === 1) ? "N/A" : `${getFileSizeFormatted(version)} / ${getPagesCount(version)}`
                };

                const updatedTimeFormatted = version.modified != null
                  ? formatTime(version.modified)
                  : formatTime(version.created);

                return (
                  <tr key={verId || idx} className="borderrow">
                    <td className="borderRight stats-th-ver">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleOpenVersionModal(version);
                        }}
                        className="ms-1"
                        style={{ color: "#3045c0fc", cursor: "pointer" }}>
                        {displayVerNo}
                      </a>
                    </td>
                    <td className="font-13 text-slate-700 borderRight stats-th-orig">
                      {sizeInfo.origDisplay}
                    </td>
                    <td className="font-13 text-slate-700 borderRight stats-th-upd">
                      {sizeInfo.updatedDisplay}
                    </td>
                    <td className="font-13 text-slate-700 borderRight stats-th-user">
                      {version.modifiedBy || version.createdBy || "-"}
                    </td>
                    <td className="font-13 text-slate-700 borderRight stats-th-time">
                      {updatedTimeFormatted}
                    </td>
                    <td className="text-center font-13 stats-th-key">
                      <a
                        href="#"
                        className="ms-1 stats-link"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleOpenVersionModal(version);
                        }}
                      >
                        {selectedLanguage?.LstLanguageCodes?.UI_ViewInfo || "View Info"}
                      </a>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Page Statistics Version Popup Modal */}
      {selectedVersionModal && (() => {
        const verId = selectedVersionModal.versionNumber;
        const displayVerNo = "V" + selectedVersionModal.versionNumber;
        const currentStats = statsDataMap[verId] || [];

        return (
          <div
            className="modalCus documentversion-history shadow stats-modal-backdrop"
            id="statisticsModal"
          >
            <div
              className="modal-dialog modal-dialog-centered modal-lg stats-modal-dialog"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg bg-white rounded-3 stats-modal-content">
                {/* Modal Header */}
                <div className="p-3 d-flex align-items-center justify-content-between bg-lightred border-bottom rounded-top-3 stats-modal-header">
                  <aside className="d-flex align-items-center flex-grow-1 min-w-0 me-3">

                    <span className={"material-icons me-2 font-20 " + (item?.chailedClass || "")} style={{ color: "#f59e0b" }}>
                      description
                    </span>
                    <h4
                      title={selectedVersionModal.fileName || item.fileName}
                      className="mb-0 text-truncate font-16 fw-bold text-slate-800"
                    >
                      {selectedVersionModal.fileName || item.fileName} - {displayVerNo}
                    </h4>
                  </aside>

                  <button
                    type="button"
                    className="btn btn-outline-danger Readerclose modalclose download"
                    onClick={() => setSelectedVersionModal(null)}
                  >
                    <span className="material-icons font-20">highlight_off</span>
                  </button>
                </div>

                {/* Modal Body Container */}
                <div className="p-4 custom-scrollbar overflow-auto flex-fill bg-slate-50">
                  <div className="bg-white border shadow-sm p-3 rounded-3 stats-card-box">
                    <div className="w-100 mb-3 d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <aside className="d-flex align-items-center flex-grow-1 min-w-0 me-3">
                        <h5 >
                          <span className="fontWeight-normal font-18 text-muted">{selectedLanguage?.LstLanguageCodes?.UI_StatisticsTop1}</span>
                        </h5>
                        <InfoToolTip anchorId={`stats-top-info-${selectedVersionModal?.id || 'v'}`} languageCode={selectedLanguage?.LstLanguageCodes?.UI_StatisticsInfo} classNameInfo="ms-1 font-16 info-Statistics-stat-manage cursor-pointer" classNameTooltip="statistics-tooltip" />
                      </aside>

                      <div className="flex-shrink-0 text-end">
                        <span className="font-13 fw-semibold">
                          {selectedLanguage?.LstLanguageCodes?.UI_TotalPages || "Total Pages"}:{" "}
                          <span style={{ color: "#d21513", fontWeight: "bold" }}>{getPagesCount(selectedVersionModal)}</span>
                        </span>
                      </div>
                    </div>

                    {/* Page Statistics Table */}
                    <div className="innertable-responsive activityLogtable-container w-100 border rounded-3 stats-table-container">
                      <table className="table mb-0 bg-white w-100 text-center stats-table">
                        <thead className="bg-lightred">
                          <tr>
                            <th className="borderRight text-center font-13 stats-th-page">
                              {selectedLanguage?.LstLanguageCodes?.UI_PageNumber || "Page Number"}
                            </th>
                            <th className="borderRight text-center font-13 stats-th-total-time">
                              <div className="d-flex align-items-center justify-content-center gap-1">
                                <span>{selectedLanguage?.LstLanguageCodes?.UI_TotalMinutes}</span>
                                <InfoToolTip anchorId={`stats-totalTime-info-${selectedVersionModal?.id || 'v'}`} languageCode={selectedLanguage?.LstLanguageCodes?.UI_StatisticsTotalTime} classNameInfo="flex-shrink-0 font-18 info-Statistics-stat-manage cursor-pointer" classNameTooltip="statistics-tooltip" />
                              </div>
                            </th>
                            <th className="borderRight text-center font-13 stats-th-total-reads">
                              <div className="d-flex align-items-center justify-content-center gap-1">
                                <span>{selectedLanguage?.LstLanguageCodes?.UI_TotalReads}</span>
                                <InfoToolTip anchorId={`stats-totalReads-info-${selectedVersionModal?.id || 'v'}`} languageCode={selectedLanguage?.LstLanguageCodes?.UI_StatisticsTotalPageReads} classNameInfo="flex-shrink-0 font-18 info-Statistics-stat-manage cursor-pointer" classNameTooltip="statistics-tooltip" />
                              </div>
                            </th>
                            <th className="text-center font-13 stats-th-session-reads">
                              <div className="d-flex align-items-center justify-content-center gap-1">
                                <span>{selectedLanguage?.LstLanguageCodes?.UI_TotalReadspersession}</span>
                                <InfoToolTip anchorId={`stats-perSessionReads-info-${selectedVersionModal?.id || 'v'}`} languageCode={selectedLanguage?.LstLanguageCodes?.UI_StatisticsTotalPageReadsPerSession} classNameInfo="flex-shrink-0 font-18 info-Statistics-stat-manage cursor-pointer" classNameTooltip="statistics-tooltip_pageReadsPerSession" />
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {isStatsLoading ? (
                            <tr>
                              <td colSpan={4} className="text-center p-4">
                                <LoadingSpinner />
                              </td>
                            </tr>
                          ) : currentStats.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-center p-4 text-slate-500">
                                <div className="no-record mt-n1 d-flex align-items-center justify-content-center w-100">
                                  <span className="material-icons me-1" style={{ color: '#ff9800' }}>report_problem</span>
                                  <span className="font-13">{selectedLanguage?.LstLanguageCodes?.UI_NoRecordsFound}</span>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            currentStats.map((stat, sIdx) => (
                              <tr key={stat.pageNumber || sIdx} className="borderrow">
                                <td className="borderRight font-13 text-center stats-td-page">{stat.pageNumber}</td>
                                <td className="borderRight font-13 text-center stats-td-total-time">{getTimeFormat(stat.totalSeconds)}</td>
                                <td className="borderRight font-13 text-center stats-td-total-reads">{stat.totalPageReads}</td>
                                <td className="font-13 text-center stats-td-session-reads">{stat.readsPerSession}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
